import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Calendar } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import BrutalButton from "@/components/common/BrutalButton";

function formatDate(d) {
    try {
        return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    } catch {
        return "";
    }
}

export default function BlogDetail() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        setPost(null);
        setError("");
        api.get(`/blog/posts/${slug}`)
            .then((r) => setPost(r.data))
            .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message));
    }, [slug]);

    if (error) {
        return (
            <section className="py-24 px-6">
                <div className="max-w-3xl mx-auto brutal-card bg-brand-orange text-white" data-testid="blog-detail-error">
                    <h2 className="font-display text-3xl font-black">Couldn't load this post</h2>
                    <p className="mt-3 font-body">{error}</p>
                    <BrutalButton as={Link} to="/blog" variant="white" className="mt-6">
                        <ArrowLeft size={16} /> Back to blog
                    </BrutalButton>
                </div>
            </section>
        );
    }

    if (!post) {
        return (
            <section className="py-24 px-6 max-w-3xl mx-auto">
                <div className="animate-pulse space-y-5">
                    <div className="h-3 w-32 bg-cream/80 border border-ink/20" />
                    <div className="h-12 bg-cream/80 border border-ink/20" />
                    <div className="h-64 bg-cream/80 border border-ink/20" />
                </div>
            </section>
        );
    }

    return (
        <article data-testid="blog-detail-page" className="py-16 md:py-24">
            <div className="max-w-3xl mx-auto px-6">
                <Link
                    to="/blog"
                    className="label-mono inline-flex items-center gap-1 hover:text-brand-blue transition-colors"
                >
                    <ArrowLeft size={14} /> All posts
                </Link>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                    {post.tag && (
                        <span className="label-mono bg-brand-yellow border-2 border-ink rounded-full px-3 py-1">
                            {post.tag}
                        </span>
                    )}
                    <span className="label-mono text-ink/60 inline-flex items-center gap-1">
                        <Calendar size={12} /> {formatDate(post.created_at)}
                    </span>
                </div>
                <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1] tracking-tighter">
                    {post.title}
                </h1>
                <p className="mt-5 font-body text-lg text-ink/70 leading-relaxed">{post.excerpt}</p>

                {post.cover_image && (
                    <div className="mt-10 brutal-card p-2 md:p-3">
                        <div className="aspect-[16/9] rounded-xl overflow-hidden border-2 border-ink">
                            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
                        </div>
                    </div>
                )}

                <div className="mt-12 prose-content space-y-6">
                    {post.body.split("\n\n").map((para, i) => (
                        <p key={i} className="font-body text-lg leading-relaxed text-ink/85">
                            {para}
                        </p>
                    ))}
                </div>

                <div className="mt-14 pt-8 border-t-2 border-ink/15 flex flex-wrap items-center justify-between gap-4">
                    <span className="label-mono text-ink/60">— {post.author_name}</span>
                    <BrutalButton as={Link} to="/contact" variant="primary">
                        Have a project? Let's talk
                    </BrutalButton>
                </div>
            </div>
        </article>
    );
}
